import logo from '@/assets/logo.svg';
import { userAtom } from '@/atoms/user.atom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { getOrgSlugFromHostname } from '@/core/domain';
import { execute } from '@/graphql/execute';
import { cn } from '@/lib/utils';
import { saveOnboardingSurveyMutation } from '@/mutations/user.mutation';
import { useAtom } from 'jotai';
import { analytics, useFeatureFlagEnabled } from '@/lib/analytics';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

interface RadioGroupProps {
  label: string;
  name: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}

const RadioGroup = ({ label, name, options, value, onChange }: RadioGroupProps) => (
  <div className='grid gap-3'>
    <Label>{label}</Label>
    <div className='flex flex-wrap gap-2'>
      {options.map((option) => (
        <button
          key={option.value}
          type='button'
          onClick={() => onChange(option.value)}
          className={cn(
            'px-4 py-2 rounded-full text-sm border transition-colors',
            value === option.value
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-background hover:bg-muted border-border',
          )}>
          {option.label}
        </button>
      ))}
    </div>
  </div>
);

export const OnboardingSurveyForm = ({ className, ...props }: React.ComponentProps<'div'>) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('organizationId');
  const slug = getOrgSlugFromHostname();
  const [me] = useAtom(userAtom);
  const { t } = useTranslation('auth');
  const [isLoading, setIsLoading] = useState(false);
  const onboardingEnabled = useFeatureFlagEnabled('onboarding-flow');

  const REFERRAL_OPTIONS = [
    { value: 'search', label: t('onboarding.survey.searchEngine') },
    { value: 'social', label: t('onboarding.survey.socialMedia') },
    { value: 'colleague', label: t('onboarding.survey.colleague') },
    { value: 'conference', label: t('onboarding.survey.conference') },
    { value: 'other', label: t('onboarding.survey.other') },
  ];

  const INDUSTRY_OPTIONS = [
    { value: 'construction', label: t('onboarding.survey.construction') },
    { value: 'mining', label: t('onboarding.survey.mining') },
    { value: 'surveying', label: t('onboarding.survey.surveying') },
    { value: 'engineering', label: t('onboarding.survey.engineering') },
    { value: 'government', label: t('onboarding.survey.government') },
    { value: 'other', label: t('onboarding.survey.other') },
  ];

  const TEAM_SIZE_OPTIONS = [
    { value: '1', label: t('onboarding.survey.justMe') },
    { value: '2-5', label: '2-5' },
    { value: '6-20', label: '6-20' },
    { value: '21-50', label: '21-50' },
    { value: '50+', label: '50+' },
  ];

  const ROLE_OPTIONS = [
    { value: 'surveyor', label: t('onboarding.survey.surveyor') },
    { value: 'engineer', label: t('onboarding.survey.engineer') },
    { value: 'project-manager', label: t('onboarding.survey.projectManager') },
    { value: 'executive', label: t('onboarding.survey.executive') },
    { value: 'other', label: t('onboarding.survey.other') },
  ];

  const [referralSource, setReferralSource] = useState('');
  const [industry, setIndustry] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [role, setRole] = useState('');

  const redirectToOrg = () => {
    // Already on the org's subdomain, just navigate to root
    navigate('/');
  };

  // Skip survey if feature flag is off or already completed/skipped
  useEffect(() => {
    if (onboardingEnabled === false || (me?.onboardingSurveyCompleted && organizationId)) {
      redirectToOrg();
    }
  }, [onboardingEnabled, me?.onboardingSurveyCompleted, organizationId]);

  const handleSubmit = async (skipped: boolean) => {
    setIsLoading(true);

    try {
      await execute(saveOnboardingSurveyMutation, {
        data: skipped
          ? { skippedAt: new Date().toISOString() }
          : {
              referralSource,
              industry,
              teamSize,
              role,
              completedAt: new Date().toISOString(),
            },
      });

      analytics.capture('Onboarding Survey Completed', {
        skipped,
        ...(skipped ? {} : { referralSource, industry, teamSize, role }),
      });

      if (!skipped) {
        toast.success(t('onboarding.survey.thanks'));
      }

      redirectToOrg();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('onboarding.survey.failedSave');
      toast.error(t('onboarding.survey.failedSave'), { description: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  if (!organizationId) {
    navigate('/auth/onboarding/org');
    return null;
  }

  return (
    <div className={cn('flex flex-col gap-6 w-full max-w-lg mx-auto', className)} {...props}>
      <Card className='overflow-hidden p-0'>
        <CardContent className='p-6 md:p-8 bg-white dark:bg-stone-900'>
          <div className='flex flex-col gap-6'>
            <div className='flex flex-col items-center text-center'>
              <img src={logo} alt='Logo' className='size-16 mb-6' />
              <h1 className='text-2xl font-bold'>{t('onboarding.survey.title')}</h1>
              <p className='text-muted-foreground text-balance'>{t('onboarding.survey.description')}</p>
            </div>

            <RadioGroup
              label={t('onboarding.survey.referralSource')}
              name='referralSource'
              options={REFERRAL_OPTIONS}
              value={referralSource}
              onChange={setReferralSource}
            />

            <RadioGroup
              label={t('onboarding.survey.industry')}
              name='industry'
              options={INDUSTRY_OPTIONS}
              value={industry}
              onChange={setIndustry}
            />

            <RadioGroup
              label={t('onboarding.survey.teamSize')}
              name='teamSize'
              options={TEAM_SIZE_OPTIONS}
              value={teamSize}
              onChange={setTeamSize}
            />

            <RadioGroup
              label={t('onboarding.survey.role')}
              name='role'
              options={ROLE_OPTIONS}
              value={role}
              onChange={setRole}
            />

            <div className='flex gap-3'>
              <Button
                type='button'
                variant='outline'
                className='flex-1'
                onClick={() => handleSubmit(true)}
                disabled={isLoading}>
                {t('onboarding.survey.skip')}
              </Button>
              <Button type='button' className='flex-1' onClick={() => handleSubmit(false)} disabled={isLoading}>
                {isLoading ? t('onboarding.survey.saving') : t('onboarding.survey.continue')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
